import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import useDemandStudioData from "./useDemandStudioData";
import useDemandStudioSectionTexts from "./useDemandStudioSectionTexts";
import useDemandStudioViewState from "./useDemandStudioViewState";
import useDemandStudioSearch from "./useDemandStudioSearch";
import useDemandStudioTabView from "./useDemandStudioTabView";
import useDemandStudioTips from "./useDemandStudioTips";
import useSnapshotUndo from "./useSnapshotUndo";
import useDemandStudioMedicalBillEdit from "./useDemandStudioMedicalBillEdit";

// defer isOpen so the false->true transition happens on an update, not mount
const useDeferredOpen = () => {
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    setIsOpen(true);
  }, []);
  return isOpen;
};

const useDemandStudio = ({ titleFallback = "Demand Letter" } = {}) => {
  const { caseId } = useParams();
  const [searchParams] = useSearchParams();
  const initialTabType = searchParams.get("tab");
  const [isSearchBarOpen, setIsSearchBarOpen] = useState(false);
  const isOpen = useDeferredOpen();

  const {
    sectionComments,
    sectionDefinitions,
    caseDetails,
    isInitialLoadPending,
    isLoading,
    isSaving,
    isRegenerating,
    regenerate,
    isAnySectionRegenerating,
    updateSectionComment,
    clearAllComments,
    handleSave,
    handleRegenerateSection,
    handlePreview,
    showPreview,
    setShowPreview,
    getSectionUiState,
    suggestedEditsSettledSignal,
    resultRequests,
    applicableSectionIds,
    fetchCaseDetails,
    hasMoreHistory,
    loadOlderUntil,
    onEditPersisted,
    isFinalizingExit
  } = useDemandStudioData({ isOpen, caseId });

  const {
    sectionTexts,
    updateSectionText,
  } = useDemandStudioSectionTexts({
    sectionDefinitions,
    resultRequests
  });

  const {
    medicalBillRecordsByClientId,
    updateMedicalBillRecordField,
    addMedicalBillRecordRow,
    removeMedicalBillRecordRow,
    isSavingManualEdit: isSavingMedicalBillEdit
  } = useDemandStudioMedicalBillEdit({
    caseId,
    caseDetails,
    sectionDefinitions,
    resultRequests,
    onEditPersisted
  });

  const {
    focusedSectionId,
    sectionRefs,
    handleSectionFocus,
    handleSectionBlur,
    handleEditSection,
    handleCardFocus,
    handleJumpToSection,
    flushSectionTextUpdates,
    isSavingManualEdit: isSavingSectionText
  } = useDemandStudioViewState({ 
    isOpen, 
    setShowPreview, 
    sectionTexts, 
    caseId,
    onEditPersisted
  });

  const {
    isTabView,
    activeTabType,
    tabs,
    visibleSectionComments,
    toggleViewMode,
    handleTabClick,
    getTabTypeForSection,
  } = useDemandStudioTabView({ sectionDefinitions, sectionComments, initialTabType });

  // keep tab-view state in refs so the callback identity stays stable across tab changes,
  // otherwise the search hook's effect would refire and reset the active match index
  const tabSyncRef = useRef({ isTabView, activeTabType, getTabTypeForSection });
  tabSyncRef.current = { isTabView, activeTabType, getTabTypeForSection };

  const handleJumpToSectionWithTabSync = useCallback((payload) => {
    const { isTabView: tabViewActive, activeTabType: currentTab, getTabTypeForSection: resolveTabType } = tabSyncRef.current;
    if (!tabViewActive) {
      handleJumpToSection(payload);
      return;
    }
    const tabType = resolveTabType(payload?.sectionId);
    if (tabType && tabType !== currentTab) {
      handleTabClick(tabType);
      requestAnimationFrame(() => handleJumpToSection(payload));
      return;
    }
    handleJumpToSection(payload);
  }, [handleTabClick, handleJumpToSection]);

  const {
    searchTerm,
    onSearchChange,
    onSearchKeyDown,
    clearSearch,
    hasSearch,
    matchCount,
    activeMatchNumber,
    activeMatch,
    goToNextMatch,
    goToPreviousMatch,
  } = useDemandStudioSearch({
    isOpen,
    showPreview,
    sectionDefinitions,
    sectionTexts,
    onJumpToSection: handleJumpToSectionWithTabSync,
  });

  const {
    handleUndo,
    isUndoLoading,
    hasUndo,
    hasRedo
  } = useSnapshotUndo({
    caseId,
    fetchCaseDetails,
    resultRequests,
    hasMoreHistory,
    loadOlderUntil,
    onEditPersisted
  });

  const {
    isOpen: isTipsOpen,
    anchorRef: tipsAnchorRef,
    toggleTips,
    closeTips,
  } = useDemandStudioTips();

  const handleFlushAndSave = useCallback(async () => {
    await flushSectionTextUpdates();
    await handleSave();
  }, [flushSectionTextUpdates, handleSave]);

  const handleFlushAndPreview = useCallback(async () => {
    await flushSectionTextUpdates();
    await handlePreview();
  }, [flushSectionTextUpdates, handlePreview]);

  const handleEditSectionWithTabSync = useCallback((sectionId) => {
    if (isTabView) {
      const tabType = getTabTypeForSection(sectionId);
      if (tabType) handleTabClick(tabType);
      requestAnimationFrame(() => handleEditSection(sectionId));
      return;
    }
    handleEditSection(sectionId);
  }, [isTabView, handleTabClick, getTabTypeForSection, handleEditSection]);

  const activeTabSectionIds = useMemo(
    () => isTabView ? visibleSectionComments.map((s) => s.id) : null,
    [isTabView, visibleSectionComments]
  );

  const clearActiveComments = useCallback(
    () => clearAllComments(activeTabSectionIds ? { sectionIds: activeTabSectionIds } : {}),
    [clearAllComments, activeTabSectionIds]
  );

  const hasActiveComments = useMemo(
    () => visibleSectionComments.some((s) => Boolean(s.comment?.trim())),
    [visibleSectionComments]
  );

  const handleTogglePreview = useCallback(() => {
    setShowPreview((prev) => !prev);
  }, [setShowPreview]);

  const handleOpenSearchBar = useCallback(() => {
    setIsSearchBarOpen(true);
    if (showPreview) {
      setShowPreview(false);
    }
  }, [setShowPreview, showPreview]);

  const caseName = caseDetails?.caseInfo?.caseName || titleFallback;
  console.log("isSaving: ", isSaving);
  console.log("isRegenerating: ", isRegenerating);
  const isPreviewLoading = isSaving || isRegenerating;
  const isManualEditProcessing = Boolean(isSavingSectionText || isSavingMedicalBillEdit);
  const shouldShowInitialLoader = !isOpen || isInitialLoadPending;

  return {
    caseId,
    caseName,
    isInitialLoadPending: shouldShowInitialLoader,
    isPreviewLoading,
    showPreview,
    isSearchBarOpen,
    isLoading,
    isSaving,
    isRegenerating,
    regenerate,
    isFinalizingExit,
    sectionComments,
    sectionDefinitions,
    sectionTexts,
    applicableSectionIds,
    caseDetails,
    hasAnyComments: hasActiveComments,
    focusedSectionId,
    sectionRefs,
    isTipsOpen,
    tipsAnchorRef,
    medicalBillRecordsByClientId,
    isTabView,
    activeTabType,
    tabs,
    visibleSectionComments,
    toggleViewMode,
    handleTabClick,
    isAnySectionRegenerating,
    isManualEditProcessing,
    handleUndo,
    isUndoLoading,
    hasUndo,
    hasRedo,
    clearAllComments: clearActiveComments,

    searchTerm,
    onSearchChange,
    onSearchKeyDown,
    clearSearch,
    hasSearch,
    matchCount,
    activeMatchNumber,
    activeMatch,
    goToNextMatch,
    goToPreviousMatch,
    
    handleSave: handleFlushAndSave,
    handleTogglePreview,
    handleOpenSearchBar,
    handlePreview: handleFlushAndPreview,
    handleRegenerateSection,
    handleSectionFocus,
    handleSectionBlur,
    handleEditSection: handleEditSectionWithTabSync,
    handleCardFocus,
    toggleTips,
    closeTips,
    updateSectionComment,
    updateSectionText,
    updateMedicalBillRecordField,
    addMedicalBillRecordRow,
    removeMedicalBillRecordRow,
    getSectionUiState,
    suggestedEditsSettledSignal,
  };
};

export default useDemandStudio;
